
import { authPost } from '@client/shared/auth/index.ts';
import { PATHS } from '@client/shared/router/paths.ts';

export const CALLBACK_DESTINATION = {
  /** The self-serve onboarding wizard's email-link landing: an invite for an org-LESS principal,
   *  where the 2-step wizard folds set-password INTO onboarding (set password → create org). An
   *  existing tenant setting/resetting a password uses {@link resetPassword} instead. */
  setPassword: PATHS.setPassword,
  /** The STANDALONE set/reset-password screen for an authenticated email-link login: a recovery
   *  reset, or a teammate invite into an already-existing org. Grant-gated, no org step: the user
   *  sets one password and lands on the app. Distinct from {@link setPassword}'s onboarding wizard. */
  resetPassword: PATHS.resetPassword,
  /** The self-serve onboarding wizard (verified onboarding principal: wired fully in ticket 07). */
  onboarding: PATHS.onboarding,
  dashboard: PATHS.home,
  login: PATHS.login,
} as const;

export type CallbackDestination = (typeof CALLBACK_DESTINATION)[keyof typeof CALLBACK_DESTINATION];

export type CallbackType = 'invite' | 'recovery';

export interface CallbackHash {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly type: CallbackType | null;
}

export function parseCallbackHash(hash: string): CallbackHash | null {
  const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
  if (fragment.length === 0) return null;

  const params = new URLSearchParams(fragment);
  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  if (!accessToken || !refreshToken) return null;

  const rawType = params.get('type');
  const type: CallbackType | null =
    rawType === 'invite' || rawType === 'recovery' ? rawType : null;

  return { accessToken, refreshToken, type };
}

export interface SessionFromEmailResult {
  readonly ok: boolean;
  readonly passwordGrant?: string;
}

export function isExpiredLinkOutcome(outcome: PostOutcome): boolean {
  return outcome.status === 401;
}

export function isSuccessOutcome(outcome: PostOutcome): boolean {
  return outcome.status >= 200 && outcome.status < 300 && outcome.body?.ok === true;
}

export interface PostOutcome {
  readonly status: number;
  readonly body: SessionFromEmailResult | null;
}

export async function postSessionFromEmail(hash: CallbackHash): Promise<PostOutcome> {
  const response = await authPost('/auth/session-from-email', 'session-from-email', {
    access_token: hash.accessToken,
    refresh_token: hash.refreshToken,
  });

  let body: SessionFromEmailResult | null = null;
  try {
    body = (await response.json()) as SessionFromEmailResult;
  } catch {
  }
  return { status: response.status, body };
}

/**
 * Decide where the callback hands off, given the hash `type` and the POST outcome.
 *
 * - The POST failed (not 2xx, or no `ok`) → the link is bad/expired → back to the sign-in gate.
 * - An INVITE or RECOVERY link sets a password. A principal with an EXISTING org (has a group,
 *   incl. setup-incomplete teammates) sets it on the standalone {@link CALLBACK_DESTINATION
 *   .resetPassword} screen; an org-less self-serve onboarder goes to the onboarding wizard
 *   landing ({@link CALLBACK_DESTINATION.setPassword}), which folds the password step into
 *   onboarding. Recovery must NOT assume an existing tenant: a RESENT owner invite arrives as
 *   `type=recovery` (Supabase `email_exists` → recovery fallback in the link minter).
 * - Otherwise the POST established a session with no password step → defer to the resolved session:
 *   a tenant goes to the dashboard, an org-less onboarding principal to the wizard.
 *
 * The `hasGroup` flag is read from the re-resolved `/auth/me` session by the caller.
 */
export function chooseDestination(input: {
  readonly type: CallbackType | null;
  readonly outcome: PostOutcome;
  readonly hasGroup: boolean;
}): CallbackDestination {
  const { type, outcome, hasGroup } = input;

  if (!isSuccessOutcome(outcome)) {
    return CALLBACK_DESTINATION.login;
  }

  // Invite OR recovery: someone with an existing org sets/resets their password on the standalone
  // screen; an org-less self-serve onboarder gets the 2-step wizard (set-password → create-org).
  // Recovery can't shortcut to the standalone screen: resent owner invites arrive as recovery.
  if (type !== null) {
    return hasGroup ? CALLBACK_DESTINATION.resetPassword : CALLBACK_DESTINATION.setPassword;
  }

  return hasGroup ? CALLBACK_DESTINATION.dashboard : CALLBACK_DESTINATION.onboarding;
}
